import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Bot, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';

export function BotPersonalizerPlugin({ serverId }: { serverId: string }) {
  const { t } = useI18n();
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const botPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'bot-personalizer');
  const isEnabled = botPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'bot-personalizer') : pluginApi.disablePlugin(serverId, 'bot-personalizer'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'bot-personalizer', config),
    {
      onSuccess: () => toast.success(t('botPersonalizer.saved')),
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    nickname: '',
    status: 'online',
    activity: ''
  });

  useEffect(() => {
    if (botPlugin?.config) {
      setConfig({ ...config, ...botPlugin.config });
    }
  }, [botPlugin]);

  if (!botPlugin) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Bot className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p>{t('botPersonalizer.unavailable')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-fuchsia-500/20 rounded-xl">
            <Bot className="w-6 h-6 text-fuchsia-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Bot Personalizer</h3>
            <p className="text-sm text-muted-foreground">{t('botPersonalizer.subtitle')}</p>
          </div>
        </div>
        <Switch checked={isEnabled} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div>
              <Label>{t('botPersonalizer.nickname')}</Label>
              <Input
                value={config.nickname}
                onChange={(e) => setConfig({ ...config, nickname: e.target.value })}
                placeholder="Community Hub Bot"
              />
            </div>

            <div>
              <Label>{t('botPersonalizer.status')}</Label>
              <Select value={config.status} onValueChange={(value) => setConfig({ ...config, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder={t('botPersonalizer.selectStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="idle">{t('botPersonalizer.idle')}</SelectItem>
                  <SelectItem value="dnd">{t('botPersonalizer.dnd')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('botPersonalizer.activity')}</Label>
              <Input
                value={config.activity}
                onChange={(e) => setConfig({ ...config, activity: e.target.value })}
                placeholder={t('botPersonalizer.activityPlaceholder')}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => updateConfig(config)} disabled={saving} className="min-w-[160px]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {t('botPersonalizer.save')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
